import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./FeeStatementFilter.css";
import { API_URL, secret_key, storedData } from './Constants';
import CryptoJS from 'crypto-js';
import { Filter } from "lucide-react";

export default function ReportFormFilter() {
  const navigate = useNavigate();

  // --- Decrypt User Data ---
  let loggedUserData = null;
  if (storedData) {
    try {
      const bytes = CryptoJS.AES.decrypt(storedData, secret_key);
      loggedUserData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (err) {
      console.error("User data decryption error:", err);
    }
  }

  const [siblings, setSiblings] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [loading, setLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);

  // ✅ Load students first
  useEffect(() => {
    if (!loggedUserData?.company_id || !loggedUserData?.parent_no) return;

    setLoading(true);

    axios
      .get(`${API_URL}/students/${loggedUserData.company_id}/${loggedUserData.parent_no}`, {
        headers: {
          Authorization: `CustomToken ${secret_key}`,
        },
      })
      .then((res) => {
        if (res.data.success) {
          setSiblings(res.data.details || []);
        }
      })
      .catch((err) => {
        console.error("Error loading students:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  // ✅ Handle student change → fetch batches for that class
  const handleStudentChange = (admission_no) => {
    setSelectedStudent(admission_no);
    setSelectedBatch(""); // reset batch

    const student = siblings.find(s => s.admission_no === admission_no);
    const classCode = student?.class;

    if (!classCode) return;

    setSelectedClass(classCode);
    setBatchLoading(true);

    axios
      .get(`${API_URL}/published_batches/${loggedUserData.company_id}/${classCode}`, {
        headers: {
          Authorization: `CustomToken ${secret_key}`,
        },
      })
      .then((res) => {
        if (res.data.success) {
          const batchList = res.data.data || [];

          setBatches(batchList);

          // ✅ Auto-pick latest
          if (batchList.length > 0) {
            const sorted = [...batchList].sort(
              (a, b) => Number(b.id) - Number(a.id)
            );
            setSelectedBatch(sorted[0].id);
          }
        } else {
          setBatches([]);
        }
      })
      .catch((err) => {
        console.error("Error loading batches:", err);
        setBatches([]);
      })
      .finally(() => setBatchLoading(false));
  };

  const handleProceed = () => {
    if (!selectedStudent) return alert("Please select a student.");
    if (!selectedBatch) return alert("Please select a batch.");

    const company_id = loggedUserData?.company_id;

    navigate(`/report_form/${company_id}/${selectedStudent}/${selectedBatch}`, {
      state: { auth: secret_key }
    });
  };

  return (
    <div className="container my-4">
      <div className="card shadow-sm rounded-4 p-4">

        {/* Title */}
        <h2 className="fw-bold d-flex align-items-center gap-2 mb-1">
          <Filter size={20} />
          Report Form Filter
        </h2>

        <p className="text-muted mb-4">
          Select a student, then choose the exam batch
        </p>

        {loading ? (
          <div className="text-center py-3">Loading students...</div>
        ) : siblings.length === 0 ? (
          <div className="alert alert-warning text-center">
            No students found.
          </div>
        ) : (
          <div className="row g-3">

            {/* Student */}
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">
                Select Student
              </label>

              <select
                className="form-select"
                value={selectedStudent}
                onChange={(e) => handleStudentChange(e.target.value)}
              >
                <option value="">-- Choose Student --</option>
                {siblings.map((s, i) => (
                  <option key={i} value={s.admission_no}>
                    {s.full_name} ({s.admission_no})
                  </option>
                ))}
              </select>
            </div>

            {/* Batch */}
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">
                Select Batch
              </label>

              <select
                className="form-select"
                value={selectedBatch}
                onChange={(e) => setSelectedBatch(e.target.value)}
                disabled={!selectedStudent || batchLoading}
              >
                <option value="">
                  {batchLoading ? "Loading batches..." : "-- Choose Batch --"}
                </option>

                {batches.map((b, i) => (
                  <option key={i} value={b.id}>
                    {`${b.description || `Batch ${b.id}`} - ${b.class_code}`}
                  </option>
                ))}
              </select>
            </div>

          </div>
        )}

        {/* Buttons */}
        <div className="d-flex flex-column flex-sm-row justify-content-between gap-3 mt-4">

          <button
            onClick={() => navigate(-1)}
            className="btn btn-outline-secondary w-100 w-sm-auto"
          >
            ← Back
          </button>

          <button
            onClick={handleProceed}
            disabled={!selectedStudent || !selectedBatch}
            className="btn btn-primary w-100 w-sm-auto"
          >
            Proceed
          </button>

        </div>

      </div>
    </div>
  );
}